import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ArenaLogo } from '@/components/common/ArenaLogo';
import { Button } from '@/components/ui/button';

export function Referral() {
  const { referrerCode } = useParams<{ referrerCode: string }>();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shopUrl, setShopUrl] = useState('');
  const showShopUrlForm = true;

  const normalizeShopUrl = (url: string): string => {
    let normalized = url.trim().toLowerCase();
    
    // Remove protocol if present
    normalized = normalized.replace(/^https?:\/\//, '');
    
    // Add .myshopify.com if not present
    if (!normalized.endsWith('.myshopify.com')) {
      // Remove any trailing slashes or domains
      normalized = normalized.replace(/\/.*$/, '').replace(/\..*$/, '');
      normalized = `${normalized}.myshopify.com`;
    }
    
    // Add https protocol
    return `https://${normalized}`;
  };

  const isValidShopName = (shopName: string): boolean => {
    // Check if shop name contains only valid characters
    const shopNameRegex = /^[a-zA-Z0-9-]+$/;
    return shopNameRegex.test(shopName) && shopName.length > 0;
  };

  const handleShopUrlSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!shopUrl.trim()) {
      setError('Please enter your Shopify store URL');
      return;
    }

    try {
      const normalizedUrl = normalizeShopUrl(shopUrl);
      const shopName = normalizedUrl.replace('https://', '').replace('.myshopify.com', '');
      
      if (!isValidShopName(shopName)) {
        setError('Please enter a valid Shopify store name (e.g., your-store, your-store.myshopify.com, or https://your-store.myshopify.com)');
        return;
      }
    } catch (error) {
      setError('Please enter a valid Shopify store URL');
      return;
    }

    if (!referrerCode) {
      setError('Invalid referral link');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const utmSource = searchParams.get('utm_source') || 'direct';
      
      // Call backend to get Shopify App Store redirect URL with shop URL
      const response = await fetch(`${import.meta.env.VITE_API_URL}/agency/referral/${referrerCode}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          shopUrl: normalizeShopUrl(shopUrl),
          utmSource
        }),
      });

      if (!response.ok) {
        // Try to get the error message from the backend
        let errorMessage = 'Invalid or expired referral link. Please check the URL and try again.';
        try {
          const errorResponse = await response.json();
          // NestJS can return message as string or array of strings
          if (errorResponse.message) {
            if (Array.isArray(errorResponse.message)) {
              errorMessage = errorResponse.message[0]; // Take first error message
            } else {
              errorMessage = errorResponse.message;
            }
          }
        } catch {
          // If we can't parse the error response, use the default message
        }
        throw new Error(errorMessage);
      }

      // Get the redirect URL from the response
      const redirectUrl = await response.text();
      
      // Redirect to Shopify App Store
      window.location.href = redirectUrl;
      
    } catch (error) {
      console.error('Referral error:', error);
      setError(error instanceof Error ? error.message : 'Invalid or expired referral link. Please check the URL and try again.');
      setIsLoading(false);
    }
  };

  if (showShopUrlForm) {
    return (
      <div className="mx-auto flex items-center justify-center min-h-screen py-8 px-4">
        <div className="w-full max-w-[1200px] flex flex-col lg:flex-row items-start justify-center gap-8 p-6 bg-card border border-border rounded-[24px] shadow-[1px_5px_10px_0px_#D1D1D11A,4px_18px_19px_0px_#D1D1D117,9px_41px_25px_0px_#D1D1D10D,15px_73px_30px_0px_#D1D1D103,24px_115px_33px_0px_#D1D1D100]">
          
          {/* Step Images - Left Side */}
          <div className="flex-1 max-w-[500px] space-y-6">
            <div className="space-y-4">
              <div className="w-full">
                <img 
                  src="/agency/referral-steps/step-1.png" 
                  alt="Step 1: Enter your store URL" 
                  className="w-full h-auto rounded-lg border border-border shadow-md"
                />
              </div>
              <div className="w-full">
                <img 
                  src="/agency/referral-steps/step-2.png" 
                  alt="Step 2: Install from App Store" 
                  className="w-full h-auto rounded-lg border border-border shadow-md"
                />
              </div>
            </div>
          </div>

          {/* Form - Right Side */}
          <div className="flex-1 max-w-[500px]">
            <div className="flex flex-col items-center justify-center gap-6 mb-4">
              <ArenaLogo className="w-12 border border-black mx-auto mb-4 rounded-[12px] bg-[#f6f6f6]" />
              <h1 className="font-bold text-[24px] text-foreground/60 leading-[24px] tracking-[0.16px] text-center align-middle">
                Welcome to Reveshare!
              </h1>
              <p className="font-normal text-[14px] w-[330px] tracking-normal text-center align-middle text-muted-foreground">
                You've been invited to join Reveshare. Please enter your Shopify store URL to continue.
              </p>
              
              <form onSubmit={handleShopUrlSubmit} className="w-full space-y-4">
                <div>
                  <label htmlFor="shopUrl" className="block text-sm font-medium text-muted-foreground mb-2">
                    Shopify Store URL
                  </label>
                  <input
                    type="text"
                    id="shopUrl"
                    value={shopUrl}
                    onChange={(e) => setShopUrl(e.target.value)}
                    placeholder="your-store.myshopify.com"
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    disabled={isLoading}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Accepts multiple formats: <span className="font-mono">your-store</span>, <span className="font-mono">your-store.myshopify.com</span>, or <span className="font-mono">https://your-store.myshopify.com</span>
                  </p>
                </div>
                
                {error && (
                  <p className="text-red-600 text-sm">{error}</p>
                )}
                
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Redirecting...
                    </div>
                  ) : (
                    'Continue to installation'
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
} 